import {TriangleDownIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, Button, ButtonGroup, IconButton} from '@primer/react'
import {RESOLUTION_REASON_OPTIONS, type ResolutionReasonType} from '@github-ui/diff-lines/conversation-types'
import {useState} from 'react'

export function ThreadResolveActions({
  onResolve,
  showReasons,
  contextLabel,
  busy = false,
  disabled = false,
  onReplyAndResolve,
  replyAndResolveDisabled = false,
}: {
  onResolve: (reason?: ResolutionReasonType) => void
  showReasons: boolean
  contextLabel?: string
  busy?: boolean
  disabled?: boolean
  /** When set, the primary button replies first and then resolves. */
  onReplyAndResolve?: () => void
  replyAndResolveDisabled?: boolean
}) {
  const [menuOpen, setMenuOpen] = useState(false)
  const isDisabled = disabled || busy
  // With a draft, the primary action replies first, like "Close with comment".
  const primaryLabel = onReplyAndResolve ? 'Reply and resolve' : 'Resolve'
  const isPrimaryDisabled = isDisabled || (!!onReplyAndResolve && replyAndResolveDisabled)
  const primaryButton = (
    <Button
      aria-label={contextLabel ? `${primaryLabel} in ${contextLabel}` : undefined}
      aria-disabled={isPrimaryDisabled}
      loading={busy}
      onClick={() => {
        if (isPrimaryDisabled) return
        if (onReplyAndResolve) onReplyAndResolve()
        else onResolve()
      }}
    >
      {primaryLabel}
    </Button>
  )
  const reasonsMenu = (
    <ActionMenu open={menuOpen} onOpenChange={setMenuOpen}>
      <ActionMenu.Anchor>
        <IconButton
          icon={TriangleDownIcon}
          aria-label={contextLabel ? `More resolve options in ${contextLabel}` : 'More resolve options'}
          aria-disabled={isDisabled}
        />
      </ActionMenu.Anchor>
      <ActionMenu.Overlay width="medium" align="end">
        <ActionList>
          {RESOLUTION_REASON_OPTIONS.map(option => (
            <ActionList.Item
              key={option.value}
              disabled={isDisabled}
              onSelect={() => {
                if (!isDisabled) onResolve(option.value)
              }}
            >
              {option.label}
              <ActionList.Description variant="block">{option.description}</ActionList.Description>
            </ActionList.Item>
          ))}
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  )
  // A ButtonGroup wraps every child in an item with a negative margin, so it is only used when there is a second button.
  return showReasons ? (
    <ButtonGroup
      onKeyDown={event => {
        // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic -- Menu dismissal must not cancel the parent editor.
        if (event.key === 'Escape' && menuOpen) {
          // Dismiss the menu without triggering the editor's empty-draft Escape handler.
          event.stopPropagation()
          setMenuOpen(false)
        }
      }}
    >
      {primaryButton}
      {reasonsMenu}
    </ButtonGroup>
  ) : (
    <div>{primaryButton}</div>
  )
}
